/**
 * Created by Ram on 12/05/2017.
 */

import React, { ChangeEvent, Component, ReactNode } from "react";
// @ts-ignore
import ReactTooltip from "react-tooltip";
import _get from "lodash.get";
import BatteryStatsGraph from "../presentation/BatteryStatsGraph";
import { BatteryRecord, Device, GraphDataPoint, Platform } from "../dec";
const d3 = require("d3");

interface Props {
    loadData: {
        devices: Device[];
        testInstance: {
            _id: string;
            devices: Device[];
        };
        sort: string;
        sortAsc: boolean;
        displayGraphByPlatform: Platform;
        isDeviceSelected: {
            [key: string]: boolean;
        };
    };
    toggleIsDeviceSelected: (deviceId: string) => void;
    updateSortState: (field: string) => void;
    toggleAllDevicesSelected: () => void;
    updateGraphByPlatform: (event: ChangeEvent) => void;
}

class BatteryStats extends Component<Props, {}> {
    renderDeviceTableRows(devices: Device[]){
        const {isDeviceSelected, testInstance} = this.props.loadData;
        return devices.map(device => {
            let dowloadLogsLink: ReactNode | string = "-";
            if (_get(device, 'logs.battery', null)) {
                dowloadLogsLink = (
                    <div>
                        <a href={`/service/testInstance/${testInstance._id}/log/${device.deviceId}/battery`}>
                            <span className="glyphicon glyphicon-save" />
                        </a>
                    </div>
                );
            }
         return (
                <tr key={device.deviceId}>
                    <td>
                        <input
                            type="checkbox"
                            checked={isDeviceSelected && isDeviceSelected[device.deviceId]}
                            onChange={() => this.props.toggleIsDeviceSelected(device.deviceId)}
                        />
                    </td>
                    <td>{device.name}</td>
                    <td>{device.osVersion || 'OS Version not recorded'}</td>
                    <td>{device.model || 'Model not recorded'}</td>
                    <td>{device.os || 'OS not recorded'}</td>
                    <td>{device.batteryEnd ? '' : <div className="survivability" />}</td>
                    <td>{device.batteryStart ? Math.round(device.batteryStart) : '-'}</td>
                    <td>{device.batteryEnd ? Math.round(device.batteryEnd) : '-'}</td>
                    <td>{device.totalDrain !== undefined ? Number(device.totalDrain).toFixed(2) : '-'}</td>
                    <td>{device.DPH !== undefined ? device.DPH.toFixed(2) : '-'}</td>
                    <td>{!isNaN(device.sdkDPH) ? device.sdkDPH.toFixed(2) : '-'}</td>
                    <td>{dowloadLogsLink}</td>
                </tr>
            );

        });
    }

    generateGraphData(devices: Device[]){
        let graphData: GraphDataPoint[] = [];
        const targetDisplayPlatform = this.props.loadData.displayGraphByPlatform;

        devices.forEach(device => {
            if(device.batteryLog && (targetDisplayPlatform === Platform.BOTH || device.os === targetDisplayPlatform)){
                const { batteryStart, batteryEnd, batteryLevelChange } = device.batteryLog;
                let batteryValues: BatteryRecord[] = [];

                if (batteryStart) batteryValues.push(batteryStart);

                if (batteryLevelChange) {
                    batteryValues = batteryValues.concat(batteryLevelChange);
                }

                if (batteryEnd) batteryValues.push(batteryEnd);

                const graphValues = batteryValues.map(value => ({
                    name: device.name,
                    time: new Date(value.time),
                    percentage: value.batteryPercentage,
                    platform: device.os || Platform.UNKNOWN,
                    isShowing: this.props.loadData.isDeviceSelected[device.deviceId]
                }));

                graphData = [...graphData, ...graphValues];
            }
        })
        return graphData;
    }

    renderAverageDrainPerHour(devices: Device[], osToDisplay) {

        let devicesWithDph = 0;
        let matchingDevices = 0;
        
        let dphSum = 0;
        let dphSDKSum = 0;
        
        const stackedDphArray = [];

        devices.forEach(device => {
            if(osToDisplay === Platform.BOTH || osToDisplay === device.os){
                matchingDevices++;
                if(device.DPH !== undefined) {
                    devicesWithDph++;
                    dphSum += device.DPH;
                    stackedDphArray.push(device.DPH);
                }

                if(!isNaN(device.sdkDPH)){                 
                    dphSDKSum += device.sdkDPH;
                }
            }
        })
        
        const averageDph = devicesWithDph ? dphSum / devicesWithDph : "N/A";
        const averageSDKDph = devicesWithDph && dphSDKSum > 0 ? (dphSDKSum / devicesWithDph).toFixed(2) : "N/A";
        const variance = devicesWithDph > 1 ? d3.variance(stackedDphArray) : "N/A";
        return (
            <div className="average-battery-stats">
                <p>{`Reporting Devices: ${devicesWithDph}/${matchingDevices}`}</p>
                <p>{`Mean Stacked DPH: ${typeof averageDph === 'number' ? averageDph.toFixed(2) : averageDph}`}</p>
                <p>{`SDK DPH: ${averageSDKDph}`}</p>
                <p>{`Variance: ${typeof variance === "number" ? variance.toFixed(2) : variance}`}</p>
            </div>
        )
    }

    render() {
        const { loadData } = this.props;
        const { devices, testInstance } = loadData;

        let arrow;
        if (loadData.sortAsc) {
            arrow = <span className="glyphicon glyphicon-triangle-top" title="Ascending" />;
        } else {
            arrow = <span className="glyphicon glyphicon-triangle-bottom" title="Descending" />;
        }

        (ReactTooltip as any).rebuild();

        return (
            <div>
                <div className="battery-analysis-main wide-view">
                    <div className="battery-results-chart">
                        <h4 className="text-center">Battery Level</h4>
                        <label>By OS:</label>
                        <select
                            id="displayGraphByPlatform"
                            onChange={this.props.updateGraphByPlatform}
                            value={loadData.displayGraphByPlatform}
                        >
                            <option value={Platform.BOTH}>Both</option>
                            <option value={Platform.ANDROID}>Android</option>
                            <option value={Platform.IOS}>iOS</option>
                        </select>
                        {this.renderAverageDrainPerHour(devices, loadData.displayGraphByPlatform)}
                        <BatteryStatsGraph graphData={this.generateGraphData(devices)} devices={devices} testInstance={testInstance} />
                    </div>
                    <div className="battery-results-table">
                        <h4 className="text-center">Battery Performance</h4>
                        <table className="table battery-performance-list">
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={Object.keys(loadData.isDeviceSelected).every(
                                                deviceId => loadData.isDeviceSelected[deviceId],
                                            )}
                                            onChange={() => this.props.toggleAllDevicesSelected()}
                                        />{' '}
                                        Select
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('name')}
                                    >
                                        Device {loadData.sort === 'name' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('osVersion')}
                                    >
                                        OS Version {loadData.sort === 'osVersion' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('model')}
                                    >
                                        Model {loadData.sort === 'model' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('platform')}
                                    >
                                        Platform {loadData.sort === 'platform' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('survivability')}
                                    >
                                        Survivability {loadData.sort === 'survivability' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('batteryStart')}
                                    >
                                        Start % {loadData.sort === 'batteryStart' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('batteryEnd')}
                                    >
                                        End % {loadData.sort === 'batteryEnd' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('totalDrain')}
                                    >
                                        Total Drain {loadData.sort === 'totalDrain' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('DPH')}
                                    >
                                        Stacked DPH {loadData.sort === 'DPH' ? arrow : ''}
                                    </th>
                                    <th
                                        className="highlight-on-hover"
                                        onClick={() => this.props.updateSortState('sdkDPH')}
                                    >
                                        SDK DPH {loadData.sort === 'sdkDPH' ? arrow : ''}
                                    </th> 
                                    <th>Download Logs</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderDeviceTableRows(devices)}</tbody>
                        </table>
                    </div>
                </div>
                <ReactTooltip />
            </div>
        ); //return
    } //render
} //BatteryStats

export default BatteryStats;
